import React, {useState} from 'react';
import {Accordion, AccordionSummary, AccordionDetails, Typography, TextField, Button, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import {styled} from "@mui/material/styles";
import {useMutation} from "@apollo/client";
import {UPDATE_WALLET_BALANCE, UPDATE_CAC_CLIENT, DELETE_CAC_CLIENT} from "../../graphql/mutations/cacClient";

export default function ClientAccordion({client, refetch}) {
    const [balance, setBalance] = useState('');
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [editedClient, setEditedClient] = useState({
        _id: client._id,
        companyName: client.companyName,
        phoneNumber: client.phoneNumber || '',
        email: client.email || '',
        address: client.address || '',
        rcNumber: client.rcNumber || '',
        active: client.active,
        beebotCacPrice: client.beebotCacPrice || 0,
        beebotCacCompanyRegPrice: client.beebotCacCompanyRegPrice || 0,
        callbackUrl: client.callbackUrl || '',
        vasApiKey: client.vasApiKey || '',
        clientRef: client.clientRef || ''
    });

    const [updateWallet, {data: walletData, error: walletError}] = useMutation(UPDATE_WALLET_BALANCE, {
        onCompleted: (data) => {
            setMessage('Balance updated successfully!');
            setMessageType('success');
            setBalance('');
            refetch();
        },
        onError: (error) => {
            setMessage('Failed to update balance. Please try again.');
            setMessageType('error');
        }
    });

    const [updateClient, {data: updateData, error: updateError}] = useMutation(UPDATE_CAC_CLIENT, {
        onCompleted: (data) => {
            setMessage('Client updated successfully!');
            setMessageType('success');
            setOpenEditDialog(false);
            refetch();
        },
        onError: (error) => {
            setMessage('Failed to update client. Please try again.');
            setMessageType('error');
        }
    });

    const [deleteClient, {data: deleteData, error: deleteError}] = useMutation(DELETE_CAC_CLIENT, {
        onCompleted: (data) => {
            setMessage('Client deleted successfully!');
            setMessageType('success');
            setOpenDeleteDialog(false);
            refetch();
        },
        onError: (error) => {
            setMessage('Failed to delete client. Please try again.');
            setMessageType('error');
        }
    });

    const handleAddBalance = async () => {
        await updateWallet(
            {
                variables: {
                    input: {
                        _id: client._id,
                        amount: balance
                    }
                }
            }
        );
    };

    const handleEditClient = async () => {
        await updateClient({
            variables: {
                input: editedClient
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditedClient({
            ...editedClient,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleDeleteClient = async () => {
        await deleteClient({
            variables: {
                id: client._id
            }
        });
    };

    const StyledElementContainer = styled('div')(({theme}) => ({
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '1rem',
    }));

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Stack spacing={2}>
                    <Typography variant="h6">Company Name: {client.companyName}</Typography>
                    <Button variant="outlined">Date Created: {dayjs(client.dateCreated).format('MMMM D, YYYY')}</Button>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Phone: {client.phoneNumber}
                    </Typography>
                </StyledElementContainer>

                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Email: {client.email}
                    </Typography>
                </StyledElementContainer>

                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Address: {client.address}
                    </Typography>
                </StyledElementContainer>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Api Key: {client.apiKey}
                    </Typography>
                </StyledElementContainer>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        VAS Api Key: {client.vasApiKey}
                    </Typography>
                </StyledElementContainer>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Client Reference: {client.clientRef}
                    </Typography>
                </StyledElementContainer>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Wallet: {client.wallet}
                    </Typography>
                </StyledElementContainer>
                <StyledElementContainer>
                    <Typography variant="body1" color="text.secondary">
                        Date Created: {dayjs(client.createdAt).format('MMMM D, YYYY')}
                    </Typography>
                </StyledElementContainer>

                {message && (
                    <StyledElementContainer>
                        <Alert severity={messageType}>{message}</Alert>
                    </StyledElementContainer>
                )}

                <Stack direction="row" spacing={2} sx={{mt: 2}}>
                    <TextField
                        label="Add Balance"
                        variant="outlined"
                        size="small"
                        value={balance}
                        onChange={(e) => setBalance(e.target.value)}
                    />
                    <Button variant="contained" color="primary" onClick={handleAddBalance}>
                        Add
                    </Button>
                    <Button variant="contained" color="secondary" onClick={() => setOpenEditDialog(true)}>
                        Edit Client
                    </Button>
                    <Button variant="contained" color="error" onClick={() => setOpenDeleteDialog(true)}>
                        Delete Client
                    </Button>
                </Stack>

                {/* Edit Client Dialog */}
                <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Edit Client: {client.companyName}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                label="Company Name"
                                name="companyName"
                                value={editedClient.companyName}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="Phone Number"
                                name="phoneNumber"
                                value={editedClient.phoneNumber}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="Email"
                                name="email"
                                value={editedClient.email}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="Address"
                                name="address"
                                value={editedClient.address}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="RC Number"
                                name="rcNumber"
                                value={editedClient.rcNumber}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="Client Reference"
                                name="clientRef"
                                value={editedClient.clientRef}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="CAC Price"
                                name="beebotCacPrice"
                                type="number"
                                value={editedClient.beebotCacPrice}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="CAC Company Reg Price"
                                name="beebotCacCompanyRegPrice"
                                type="number"
                                value={editedClient.beebotCacCompanyRegPrice}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="Callback URL"
                                name="callbackUrl"
                                value={editedClient.callbackUrl}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <TextField
                                label="VAS API Key"
                                name="vasApiKey"
                                value={editedClient.vasApiKey}
                                onChange={handleInputChange}
                                fullWidth
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editedClient.active}
                                        onChange={handleInputChange}
                                        name="active"
                                    />
                                }
                                label="Active"
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                        <Button onClick={handleEditClient} variant="contained" color="primary">
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                    <DialogTitle>Delete Client</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete <strong>{client.companyName}</strong>? 
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                        <Button onClick={handleDeleteClient} variant="contained" color="error">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
}
